"use client";

import { motion } from "framer-motion";
import { Ship, TrendingUp, Users, Globe, CheckCircle } from "lucide-react";
import SectionHero from "../components/SectionHero";
import { useEffect } from "react";

const ShipChartering = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const ownerServices = [
    "Identifying suitable employment options (spot, time charter, contracts)",
    "Presenting vessel details accurately to the market",
    "Negotiating terms balancing earnings with risk",
    "Evaluating voyage impacts on vessel health",
  ];

  const chartererServices = [
    "Matching cargo requirements with compliant tonnage",
    "Verifying vessel technical and documentation aspects",
    "Clear communication with owners",
    "Monitoring voyage execution coordination",
  ];

  return (
    <div className="pt-20">
      <SectionHero
        title="Ship Chartering"
        subtitle="Connecting Cargo and Tonnage in a Practical Way"
        description="Chartering decisions can make the difference between a profitable year and a difficult one. We support both owners and charterers in finding practical, sustainable employment solutions."
      />

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100">
                <Ship className="h-6 w-6 text-teal-600" />
              </div>
              <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
                For Shipowners
              </h2>
              <p className="mb-6 text-slate-700">
                Our objective is to secure employment that supports long-term
                vessel health, not just short-term revenue. We work with owners
                to understand their operational constraints and commercial
                goals.
              </p>
              <ul className="space-y-3">
                {ownerServices.map((service, index) => (
                  <li
                    key={index}
                    className="flex items-start space-x-3 text-slate-700"
                  >
                    <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-teal-600" />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl bg-slate-50 p-8"
            >
              <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100">
                <Users className="h-6 w-6 text-teal-600" />
              </div>
              <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
                For Charterers
              </h2>
              <p className="mb-6 text-slate-700">
                We help charterers find compliant tonnage that matches their
                operational requirements and trading pattern. Accurate vessel
                information and clear communication prevent misunderstandings.
              </p>
              <ul className="space-y-3">
                {chartererServices.map((service, index) => (
                  <li
                    key={index}
                    className="flex items-start space-x-3 text-slate-700"
                  >
                    <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-teal-600" />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
              Market Insight & Expertise
            </h2>
            <p className="mx-auto max-w-3xl text-slate-600">
              We follow trade patterns, seasonal trends, and regulatory
              developments that can impact chartering decisions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0 }}
              className="rounded-xl bg-white p-8 shadow-lg"
            >
              <Globe className="mb-4 h-8 w-8 text-teal-600" />
              <h3 className="mb-3 text-lg font-bold text-slate-900">
                Trade Patterns
              </h3>
              <p className="text-slate-600">
                Understanding seasonal trends and regional demand to optimize
                vessel employment strategies.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-xl bg-white p-8 shadow-lg"
            >
              <TrendingUp className="mb-4 h-8 w-8 text-teal-600" />
              <h3 className="mb-3 text-lg font-bold text-slate-900">
                Market Trends
              </h3>
              <p className="text-slate-600">
                Staying ahead of market developments to identify opportunities
                and mitigate risks.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="rounded-xl bg-white p-8 shadow-lg"
            >
              <CheckCircle className="mb-4 h-8 w-8 text-teal-600" />
              <h3 className="mb-3 text-lg font-bold text-slate-900">
                Regulatory Updates
              </h3>
              <p className="text-slate-600">
                Keeping clients informed of changing regulations and compliance
                requirements.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShipChartering;
